import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';

import { FavoriteItemGroupDTO, ProductDetailDTO } from '../../../../api/api';
import { CameraIcon } from '../../../../assets/icons/CameraIcon';
import { ShopAddIcon } from '../../../../assets/icons/ShopAddIcon';
import { SearchColumnFilter, selectAutocompleteFn, SelectAutocompleteFn } from '../../../../blocks/table/TableFilterUtils';
import IconButton from '../../../../components/icon-button/IconButton';
import useTableInstance from '../../../../hooks/useTableInstance';
import { GoodsData } from '../../../../models/Goods';
import n1LightTheme from '../../../../theme/light-theme';
import { AddToListIcon } from '../../../../assets/icons/AddToListIcon';

export const useGoodsList = (
  goods: ProductDetailDTO[],
  selectGoods: (goodsId?: string, goodsName?: string, amount?: number) => void,
  addToFavoriteList?: (goodId: string) => void,
  showDetails?: (goodsId: GoodsData) => void,
  groups?: FavoriteItemGroupDTO[]
) => {
  const data = useMemo((): GoodsData[] => {
    if (!goods?.length) return [];

    return goods
      .map((good) => {
        return {
          goodsId: good.itemId ? good.itemId : '',
          goodsName: good.name ?? '-',
          searchName: good.searchName ?? '-',
          goodsGroupName:
            good.itemGroupName && good.itemGroupId
              ? good.itemGroupName + ` (${good.itemGroupId})`
              : good.itemGroupName ?? '' + good.itemGroupId ?? '',
          pictureLinks: good.pictureLinks,
          favorite: good.favorite
        } as GoodsData;
      })
      .sort((a, b) => Number(b.favorite) - Number(a.favorite));
  }, [goods]);

  const columns = useMemo(() => {
    const _columns = [
      {
        Header: 'Vare nr.',
        accessor: 'goodsId',
        width: 100,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Varenavn',
        accessor: 'goodsName',
        minWidth: 150,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Gruppenavn',
        accessor: 'goodsGroupName',
        minWidth: 175,
        Filter: SelectAutocompleteFn,
        filter: selectAutocompleteFn
      },
      {
        Header: 'Søgt navn',
        accessor: 'searchName',
        minWidth: 50,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: '',
        accessor: 'actions',
        minWidth: 50,
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<GoodsData>) => {
          const { cell } = cellProps;
          return (
            <IconButton onClick={() => selectGoods(cell.row.original.goodsId, cell.row.original.goodsName, 1)}>
              <ShopAddIcon size="24px" color={n1LightTheme.palette.text.primary} />
            </IconButton>
          );
        }
      }
    ] as Column<GoodsData>[];

    // Only show 'product-details' column if showDetails callback is set
    showDetails &&
      _columns.splice(0, 0, {
        Header: '',
        accessor: 'available',
        width: 70,
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<GoodsData>) => {
          const { cell } = cellProps;
          const pictureLinks = cell.row.original.pictureLinks ?? [];
          return (
            <IconButton
              variant="outlined"
              disabled={pictureLinks?.length === 0 || !pictureLinks}
              onClick={() => showDetails && cell.row.original.goodsId && showDetails(cell.row.original)}
            >
              <CameraIcon size="20px" />
            </IconButton>
          );
        }
      });

    // Only show 'Add to favorite list'-column if addFavorite callback is set
    addToFavoriteList &&
      groups &&
      groups?.length > 0 &&
      _columns.splice(0, 0, {
        Header: '',
        accessor: 'favorite',
        width: 70,
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<GoodsData>) => {
          const { cell } = cellProps;

          return (
            <IconButton variant="outlined" onClick={() => addToFavoriteList(cell.row.original.goodsId ?? '')}>
              <AddToListIcon strokeWidth="0" size="24px" />
            </IconButton>
          );
        }
      });

    return _columns;
  }, [addToFavoriteList, selectGoods, showDetails, groups]);

  const tableInstance = useTableInstance<GoodsData>(data, columns, {
    initialState: { pageSize: 50, pageIndex: 0 }
  });

  return { tableInstance };
};
